import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  Tooltip,
  Zoom,
  Fade
} from '@mui/material';
import {
  Star,
  StarBorder,
  StarHalf,
  Favorite,
  FavoriteBorder,
  ThumbUp,
  ThumbDown,
  SentimentVeryDissatisfied,
  SentimentDissatisfied,
  SentimentNeutral,
  SentimentSatisfied,
  SentimentVerySatisfied
} from '@mui/icons-material';

const RATING_SYSTEMS = {
  STARS: {
    name: 'Stars',
    range: [1, 5],
    icons: {
      empty: StarBorder,
      half: StarHalf,
      full: Star
    },
    colors: {
      active: '#FFD700',
      inactive: '#E0E0E0',
      hover: '#FFA000'
    },
    labels: ['Terrible', 'Poor', 'Average', 'Good', 'Excellent']
  },
  HEARTS: {
    name: 'Hearts',
    range: [1, 5],
    icons: {
      empty: FavoriteBorder,
      full: Favorite
    },
    colors: {
      active: '#FF4081',
      inactive: '#E0E0E0',
      hover: '#F50057'
    },
    labels: ['Hate', 'Dislike', 'Neutral', 'Like', 'Love']
  },
  THUMBS: {
    name: 'Thumbs',
    range: [-1, 1],
    icons: {
      negative: ThumbDown,
      positive: ThumbUp
    },
    colors: {
      positive: '#4CAF50',
      negative: '#F44336',
      inactive: '#E0E0E0'
    },
    labels: ['Thumbs Down', 'Thumbs Up']
  },
  SENTIMENT: {
    name: 'Sentiment',
    range: [1, 5],
    icons: [
      SentimentVeryDissatisfied,
      SentimentDissatisfied,
      SentimentNeutral,
      SentimentSatisfied,
      SentimentVerySatisfied
    ],
    colors: {
      1: '#F44336',
      2: '#FF9800',
      3: '#FFC107',
      4: '#8BC34A',
      5: '#4CAF50',
      inactive: '#E0E0E0'
    },
    labels: ['Awful', 'Bad', 'Okay', 'Good', 'Amazing']
  },
  TOMATO_POPCORN: {
    name: 'Tomato & Popcorn',
    range: [-5, 5],
    icons: {
      tomato: '🍅',
      popcorn: '🍿'
    },
    colors: {
      tomato: '#FF4444',
      popcorn: '#FFA500',
      inactive: '#E0E0E0'
    },
    labels: {
      '-5': 'Rotten',
      '-4': 'Very Bad',
      '-3': 'Bad',
      '-2': 'Poor',
      '-1': 'Below Average',
      '1': 'Above Average',
      '2': 'Good',
      '3': 'Very Good',
      '4': 'Great',
      '5': 'Masterpiece'
    }
  }
};

const PremiumRatingSystem = ({ 
  value = 0, 
  onChange, 
  readOnly = false, 
  size = 'medium',
  system = 'STARS',
  showLabel = true,
  showValue = true,
  allowHalf = false,
  animated = true,
  compact = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [hoverValue, setHoverValue] = useState(null);
  
  const config = RATING_SYSTEMS[system];
  const iconSize = size === 'small' ? (isMobile ? 18 : 20) : size === 'large' ? (isMobile ? 36 : 40) : (isMobile ? 24 : 28);
  const displayValue = hoverValue !== null ? hoverValue : value;
  
  const handleClick = (newValue) => {
    if (!readOnly && onChange) {
      onChange(newValue);
    }
  };

  const handleMouseEnter = (newValue) => {
    if (!readOnly && !isMobile) {
      setHoverValue(newValue);
    }
  };

  const handleMouseLeave = () => {
    if (!readOnly) {
      setHoverValue(null);
    }
  };

  const renderStarSystem = () => {
    const stars = [];
    const [min, max] = config.range;
    
    for (let i = min; i <= max; i++) {
      const isActive = displayValue >= i;
      const isHalf = allowHalf && displayValue === i - 0.5;
      
      let IconComponent = config.icons.empty;
      if (isActive) IconComponent = config.icons.full;
      else if (isHalf) IconComponent = config.icons.half;
      
      stars.push(
        <Tooltip key={i} title={config.labels[i - 1]} placement="top">
          <IconButton
            onClick={() => handleClick(i)}
            onMouseEnter={() => handleMouseEnter(i)}
            onMouseLeave={handleMouseLeave}
            disabled={readOnly}
            sx={{
              p: compact ? (isMobile ? 0.25 : 0.25) : (isMobile ? 0.5 : 0.5),
              minWidth: isMobile ? 36 : 'auto',
              minHeight: isMobile ? 36 : 'auto',
              color: isActive || isHalf ? config.colors.active : config.colors.inactive,
              transition: 'all 0.2s ease',
              '&:hover': !readOnly ? {
                color: config.colors.hover,
                transform: animated ? 'scale(1.1)' : 'none'
              } : {},
              '&:disabled': {
                color: isActive || isHalf ? config.colors.active : config.colors.inactive,
                opacity: isActive || isHalf ? 1 : 0.3
              }
            }}
          >
            <IconComponent sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>
      );
    }
    
    return stars;
  };

  const renderHeartSystem = () => {
    const hearts = [];
    const [min, max] = config.range;
    
    for (let i = min; i <= max; i++) {
      const isActive = displayValue >= i;
      const IconComponent = isActive ? config.icons.full : config.icons.empty;
      
      hearts.push(
        <Tooltip key={i} title={config.labels[i - 1]} placement="top">
          <IconButton
            onClick={() => handleClick(i)}
            onMouseEnter={() => handleMouseEnter(i)}
            onMouseLeave={handleMouseLeave}
            disabled={readOnly}
            sx={{
              p: compact ? (isMobile ? 0.25 : 0.25) : (isMobile ? 0.5 : 0.5),
              minWidth: isMobile ? 36 : 'auto',
              minHeight: isMobile ? 36 : 'auto',
              color: isActive ? config.colors.active : config.colors.inactive,
              transition: 'all 0.2s ease',
              '&:hover': !readOnly ? {
                color: config.colors.hover,
                transform: animated ? 'scale(1.2)' : 'none'
              } : {},
              '&:disabled': {
                color: isActive ? config.colors.active : config.colors.inactive,
                opacity: isActive ? 1 : 0.3
              }
            }}
          >
            <IconComponent sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>
      );
    }
    
    return hearts;
  };

  const renderThumbsSystem = () => {
    return (
      <Box display="flex" gap={1}>
        <Tooltip title="Thumbs Down" placement="top">
          <IconButton
            onClick={() => handleClick(-1)}
            disabled={readOnly}
            sx={{
              p: compact ? (isMobile ? 0.75 : 0.5) : (isMobile ? 1.5 : 1),
              minWidth: isMobile ? 48 : 'auto',
              minHeight: isMobile ? 48 : 'auto',
              color: displayValue === -1 ? config.colors.negative : config.colors.inactive,
              transition: 'all 0.2s ease',
              opacity: displayValue === -1 ? 1 : (readOnly ? 0.3 : 0.6),
              '&:hover': !readOnly ? {
                color: config.colors.negative,
                opacity: 1,
                transform: animated ? 'scale(1.1)' : 'none'
              } : {},
              '&:disabled': {
                color: displayValue === -1 ? config.colors.negative : config.colors.inactive,
                opacity: displayValue === -1 ? 1 : 0.3
              }
            }}
          >
            <config.icons.negative sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Thumbs Up" placement="top">
          <IconButton
            onClick={() => handleClick(1)}
            disabled={readOnly}
            sx={{
              p: compact ? (isMobile ? 0.75 : 0.5) : (isMobile ? 1.5 : 1),
              minWidth: isMobile ? 48 : 'auto',
              minHeight: isMobile ? 48 : 'auto',
              color: displayValue === 1 ? config.colors.positive : config.colors.inactive,
              transition: 'all 0.2s ease',
              opacity: displayValue === 1 ? 1 : (readOnly ? 0.3 : 0.6),
              '&:hover': !readOnly ? {
                color: config.colors.positive,
                opacity: 1,
                transform: animated ? 'scale(1.1)' : 'none'
              } : {},
              '&:disabled': {
                color: displayValue === 1 ? config.colors.positive : config.colors.inactive,
                opacity: displayValue === 1 ? 1 : 0.3
              }
            }}
          >
            <config.icons.positive sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const renderSentimentSystem = () => {
    const sentiments = [];
    
    for (let i = 1; i <= 5; i++) {
      const IconComponent = config.icons[i - 1];
      const isActive = displayValue === i;
      
      sentiments.push(
        <Tooltip key={i} title={config.labels[i - 1]} placement="top">
          <IconButton
            onClick={() => handleClick(i)}
            onMouseEnter={() => handleMouseEnter(i)}
            onMouseLeave={handleMouseLeave}
            disabled={readOnly}
            sx={{
              p: compact ? (isMobile ? 0.5 : 0.25) : (isMobile ? 1 : 0.5),
              minWidth: isMobile ? 44 : 'auto',
              minHeight: isMobile ? 44 : 'auto',
              color: isActive ? config.colors[i] : config.colors.inactive,
              opacity: isActive ? 1 : (readOnly ? 0.3 : 0.6),
              transition: 'all 0.2s ease',
              '&:hover': !readOnly ? {
                color: config.colors[i],
                opacity: 1,
                transform: animated ? 'scale(1.15)' : 'none'
              } : {},
              '&:disabled': {
                color: isActive ? config.colors[i] : config.colors.inactive,
                opacity: isActive ? 1 : 0.3
              }
            }}
          >
            <IconComponent sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>
      );
    }
    
    return sentiments;
  };

  const renderTomatoPopcornSystem = () => {
    const items = [];
    
    // Tomatoes (-5 to -1)
    for (let i = -5; i <= -1; i++) {
      const isActive = displayValue === i;
      items.push(
        <Tooltip key={i} title={config.labels[i]} placement="top">
          <Box
            onClick={() => !readOnly && handleClick(i)}
            onMouseEnter={() => handleMouseEnter(i)}
            onMouseLeave={handleMouseLeave}
            sx={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: compact ? 0.15 : (isMobile ? 0.25 : 0.5),
              cursor: readOnly ? 'default' : 'pointer',
              opacity: isActive ? 1 : (readOnly ? 0.4 : 0.6),
              transform: isActive ? 'scale(1.1)' : 'scale(1)',
              transition: 'all 0.2s ease',
              '&:hover': !readOnly ? {
                opacity: 1,
                transform: animated ? 'scale(1.15)' : 'scale(1.1)'
              } : {},
              minWidth: isMobile ? 28 : 40,
              minHeight: isMobile ? 28 : 40
            }}
          >
            <span style={{ 
              fontSize: isMobile ? iconSize * 0.8 : iconSize,
              filter: isActive ? 'brightness(1.2) saturate(1.3)' : 'brightness(0.7) saturate(0.8)'
            }}>
              {config.icons.tomato}
            </span>
            {isActive && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  bgcolor: 'error.main',
                  color: 'white',
                  borderRadius: '50%',
                  minWidth: isMobile ? 16 : 18,
                  minHeight: isMobile ? 16 : 18,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile ? '0.6rem' : '0.7rem',
                  fontWeight: 'bold'
                }}
              >
                {Math.abs(i)}
              </Box>
            )}
          </Box>
        </Tooltip>
      );
    }
    
    // Popcorn (1 to 5)
    for (let i = 1; i <= 5; i++) {
      const isActive = displayValue === i;
      items.push(
        <Tooltip key={i} title={config.labels[i]} placement="top">
          <Box
            onClick={() => !readOnly && handleClick(i)}
            onMouseEnter={() => handleMouseEnter(i)}
            onMouseLeave={handleMouseLeave}
            sx={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: compact ? 0.15 : (isMobile ? 0.25 : 0.5),
              cursor: readOnly ? 'default' : 'pointer',
              opacity: isActive ? 1 : (readOnly ? 0.4 : 0.6),
              transform: isActive ? 'scale(1.1)' : 'scale(1)',
              transition: 'all 0.2s ease',
              '&:hover': !readOnly ? {
                opacity: 1,
                transform: animated ? 'scale(1.15)' : 'scale(1.1)'
              } : {},
              minWidth: isMobile ? 28 : 40,
              minHeight: isMobile ? 28 : 40
            }}
          >
            <span style={{ 
              fontSize: isMobile ? iconSize * 0.8 : iconSize,
              filter: isActive ? 'brightness(1.2) saturate(1.3)' : 'brightness(0.7) saturate(0.8)'
            }}>
              {config.icons.popcorn}
            </span>
            {isActive && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  bgcolor: 'success.main',
                  color: 'white',
                  borderRadius: '50%',
                  minWidth: isMobile ? 16 : 18,
                  minHeight: isMobile ? 16 : 18,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile ? '0.6rem' : '0.7rem',
                  fontWeight: 'bold'
                }}
              >
                {i}
              </Box>
            )}
          </Box>
        </Tooltip>
      );
    }
    
    return items;
  };

  const renderRatingSystem = () => {
    switch (system) {
      case 'HEARTS':
        return renderHeartSystem();
      case 'THUMBS':
        return renderThumbsSystem();
      case 'SENTIMENT':
        return renderSentimentSystem();
      case 'TOMATO_POPCORN':
        return renderTomatoPopcornSystem();
      case 'STARS':
      default:
        return renderStarSystem();
    }
  };

  const getRatingLabel = () => {
    if (value === 0) return 'No rating';
    
    switch (system) {
      case 'TOMATO_POPCORN':
        return config.labels[value] || 'No rating';
      case 'THUMBS':
        return value === 1 ? 'Thumbs Up' : value === -1 ? 'Thumbs Down' : 'No rating';
      default:
        const index = system === 'SENTIMENT' ? value - 1 : Math.max(0, value - 1);
        return config.labels[index] || 'No rating';
    }
  };

  return (
    <Box>
      <Box 
        display="flex" 
        alignItems="center" 
        gap={compact ? 0.25 : 0.5}
        flexWrap="wrap"
        justifyContent={isMobile ? "center" : "flex-start"}
        sx={{
          maxWidth: isMobile && system === 'TOMATO_POPCORN' ? '100%' : 'none'
        }}
      >
        {animated ? (
          <Fade in={true} timeout={500}>
            <Box 
              display="flex" 
              alignItems="center" 
              gap={compact ? 0.25 : 0.5}
              flexWrap="wrap"
              justifyContent={isMobile ? "center" : "flex-start"}
            >
              {renderRatingSystem()}
            </Box>
          </Fade>
        ) : (
          <Box 
            display="flex" 
            alignItems="center" 
            gap={compact ? 0.25 : 0.5}
            flexWrap="wrap"
            justifyContent={isMobile ? "center" : "flex-start"}
          >
            {renderRatingSystem()}
          </Box>
        )}
      </Box>
      
      {(showLabel || showValue) && (
        <Box mt={1} display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {showValue && value !== 0 && (
            <Chip
              label={
                system === 'TOMATO_POPCORN' ? `${value > 0 ? '+' : ''}${value}` :
                system === 'THUMBS' ? (value === 1 ? '👍' : '👎') :
                system === 'SENTIMENT' ? `${value}/5` :
                system === 'HEARTS' ? `${value}/5` :
                `${value}/5`
              }
              size="small"
              color={
                system === 'TOMATO_POPCORN' ? (value > 0 ? 'success' : 'error') :
                system === 'THUMBS' ? (value === 1 ? 'success' : 'error') :
                system === 'SENTIMENT' ? (value >= 4 ? 'success' : value >= 3 ? 'warning' : 'error') :
                'primary'
              }
              sx={{ 
                fontWeight: 600,
                minWidth: 40
              }}
            />
          )}
          {showLabel && (
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ 
                fontWeight: 500,
                textAlign: isMobile ? 'center' : 'left'
              }}
            >
              {getRatingLabel()}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PremiumRatingSystem;